import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Stack } from '@mui/material';
import React, { FC, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { IOrganisationForm, IParams, Page, Widget } from '../../../shared';
import {
    OrganisationAddressInputs,
    OrganisationContactInputs,
    OrganisationDataInputs,
    OrganisationPreferredPostalCodeInputs,
} from '../../components';
import { OrganisationSettingInputs } from '../../components/organisation-inputs/organisation-setting-inputs.component';
import { useOrganisation, useSaveOrganisation } from '../../hooks/organisation.hook';
import { organisationFromFormMapper, organisationToFormMapper } from '../../mappers';
import { useOrganisationSchema } from '../../validators';

export const OrganisationEditPage: FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const { id } = useParams<keyof IParams>() as IParams;
    const { data: organisation } = useOrganisation(id);
    const { mutateAsync: saveOrganisation, isPending } = useSaveOrganisation();

    const schema = useOrganisationSchema();
    const form = useForm<IOrganisationForm>({
        resolver: yupResolver(schema),
        defaultValues: { defaultSupportPeriod: 52 },
        mode: 'all',
    });

    useEffect(() => {
        if (organisation) form.reset(organisationToFormMapper(organisation));
    }, [organisation, form]);

    const onSubmit = async (data: IOrganisationForm) => {
        await saveOrganisation({
            data: organisationFromFormMapper(data),
            messages: {
                onUpdateSuccessMessage: t('updateOrganisationSuccess'),
                onUpdateErrorMessage: t('updateOrganisationError'),
                onCreateSuccessMessage: t('createOrganisationSuccess'),
                onCreateErrorMessage: t('createOrganisationError'),
            },
        });
        navigate('/admin/organisations');
    };

    const actions = [
        <Button onClick={() => navigate('/admin/organisations')} color="secondary">
            {t('cancel')}
        </Button>,
        <Button
            variant="contained"
            color="primary"
            onClick={form.handleSubmit(onSubmit)}
            disabled={isPending}
            type="submit"
        >
            {t('save')}
        </Button>,
    ];

    return (
        <Page title={organisation?.name || t('newOrganisation')} actions={actions} onBack={() => navigate(-1)}>
            <FormProvider {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                    <Widget footer={[...actions].reverse()}>
                        <Stack spacing={2}>
                            <OrganisationDataInputs />
                            <OrganisationSettingInputs />
                            <OrganisationAddressInputs />
                            <OrganisationPreferredPostalCodeInputs />
                            <OrganisationContactInputs />
                        </Stack>
                    </Widget>
                </form>
            </FormProvider>
        </Page>
    );
};
